import { Component } from '@theme/component';

/**
 * @typedef {Object} IngredientCardRefs
 * @property {HTMLButtonElement} [expandButton] - The expand button element
 */

/**
 * Ingredient card component that opens a modal/drawer with ingredient details.
 *
 * @extends {Component<IngredientCardRefs>}
 */
class NcnIngredientCard extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Add click handler for expand button
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    super.disconnectedCallback();
  }

  /**
   * Opens modal when card or expand button is clicked
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    // Don't prevent default on links if any exist in the card
    const target = /** @type {Element} */ (event.target);
    if (target.closest('a')) return;

    event.preventDefault();
    this.#openModal();
  };

  /**
   * Opens modal when Enter or Space is pressed
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      // Don't trigger if focused on the button (it has its own click handler)
      if (event.target === this.refs.expandButton) return;

      event.preventDefault();
      this.#openModal();
    }
  };

  /**
   * Opens the ingredient modal
   */
  #openModal() {
    const modalId = this.dataset.modalId;
    if (!modalId) {
      console.warn('NcnIngredientCard: data-modal-id attribute missing');
      return;
    }

    /** @type {HTMLElement & { showModal?: () => void } | null} */
    const modalComponent = document.getElementById(modalId);
    if (!modalComponent) {
      console.warn(`NcnIngredientCard: Modal element "${modalId}" not found`);
      return;
    }

    if (typeof modalComponent.showModal !== 'function') {
      console.warn(`NcnIngredientCard: Modal "${modalId}" does not have showModal method`);
      return;
    }

    try {
      modalComponent.showModal();
    } catch (error) {
      console.error('NcnIngredientCard: Failed to open modal', error);
    }
  }
}

if (!customElements.get('ncn-ingredient-card')) {
  customElements.define('ncn-ingredient-card', NcnIngredientCard);
}
